/**
 * Plugin-version + event-type endpoints for the IntegrationDetails admin
 * editor (Phase 6f). Injected into the shared `integrationApi`.
 *
 * Live wire (legacy IntegrationServices, all under /integration):
 *   GET   /v1/event[?eventType=<type>]                        → { eventsCatalog: [...] }
 *   GET   /v1/plugin/{id}/version/{vid}                       → { integratorId, pluginName, pluginVersion: {...} }
 *   POST  /v1/plugin/{id}/version            (create initial) → { pluginVersion: {...} }
 *   PATCH /v1/plugin/{id}/version/{vid}      (update)         → { pluginVersion: {...} }
 *   POST  /v1/plugin/{id}/version/{vid}/clone                 → { pluginVersion: {...} }
 *   PATCH /v1/plugin/{id}/version/{vid}/promote (publish)     → (no body of note)
 */
import { services } from '@config/services';
import { integrationApi } from './_base';
import type { EventTypes, PluginVersionDetails, PluginEvent } from '../IntegrationDetails/types';

/** Body for a version create/update — either the variables slice or the events slice. */
export interface PluginVersionUpdate {
  variables?: Record<string, string | undefined>;
  readOnlyVariableKeys?: string[];
  events?: PluginEvent[];
}

interface PluginVersionEnvelope {
  pluginVersion: PluginVersionDetails;
  integratorId?: string;
  pluginName?: string;
}

/** The full version detail the host page reads (variables + events + meta). */
export interface PluginVersionFull extends PluginVersionDetails {
  integratorId?: string;
  pluginName?: string;
}

/**
 * Template facts about a plugin, from `GET /v1/plugin/{id}/metadata`. Every
 * field is null on an ordinary plugin — "is this a template instance?" is a
 * legitimate question about any plugin, so the service answers rather than
 * erroring.
 *
 * `templateEventNames` carries three distinct answers and callers MUST tell
 * them apart:
 *   - `null`/absent — not an instance; there is no inherited behaviour.
 *   - `[]` — an instance whose template binds no events. The OrderMesh
 *     Fulfillment blueprint: accept per `acceptanceMode`, run no integration.
 *   - non-empty — an instance with curated behaviour that does run.
 *
 * Names only: the snippets behind these events belong to the template and are
 * deliberately not exposed here.
 */
/**
 * One variable a plugin version effectively declares, described without its
 * value. From `GET .../variableNames`.
 */
export interface PluginVariableInfo {
  name: string;
  /** Whether this variable may be edited at all — for an instance, the union of
   * the keys the template locks and the ones the version itself locks. */
  isReadOnly: boolean;
  /** Whether the pinned template version declares this variable. */
  isFromTemplate: boolean;
  /** Whether the template already supplies a value, as opposed to declaring the
   * key empty for the instance owner to fill. Presence only — never the value. */
  hasTemplateValue: boolean;
  /** Whether this version supplies its own value, overriding the template's. */
  isOverridden: boolean;
}

/** Response of `GET /v1/plugin/{id}/version/{vid}/variableNames`. */
export interface PluginVariableNames {
  variableNames?: string[];
  isTemplateInstance?: boolean;
  variables?: PluginVariableInfo[];
}

export interface PluginMetadata {
  templateId?: string | null;
  acceptanceMode?: string | null;
  pluginType?: string | null;
  templateVersionId?: string | null;
  templateEventNames?: string[] | null;
}

/**
 * Exported request builders — the single source of truth for each endpoint's
 * URL + method + body. `query:` below hands these straight to RTK Query, and
 * the wire-shape-lock test asserts them directly (fetchBaseQuery binds the
 * global fetch at module load, so request interception isn't reliable in
 * tests — asserting the builder is the portal2 convention, see
 * `api.phase5.test.ts`).
 */
export const versionRequestBuilders = {
  getEventTypes: (args?: { eventType?: string } | void) => {
    const eventType = args && 'eventType' in args ? args.eventType : undefined;
    const qs = eventType ? `?eventType=${encodeURIComponent(eventType)}` : '';
    return { url: `${services.integration}/v1/event${qs}`, method: 'GET' as const };
  },
  getPluginVersionFull: ({ pluginId, pluginVersionId }: { pluginId: string; pluginVersionId: string }) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version/${encodeURIComponent(pluginVersionId)}`,
    method: 'GET' as const,
  }),
  createPluginVersion: ({ pluginId, data }: { pluginId: string; data: PluginVersionUpdate }) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version`,
    method: 'POST' as const,
    body: data,
  }),
  updatePluginVersion: ({
    pluginId,
    pluginVersionId,
    data,
  }: {
    pluginId: string;
    pluginVersionId: string;
    data: PluginVersionUpdate;
  }) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version/${encodeURIComponent(pluginVersionId)}`,
    method: 'PATCH' as const,
    body: data,
  }),
  clonePluginVersion: ({ pluginId, pluginVersionId }: { pluginId: string; pluginVersionId: string }) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version/${encodeURIComponent(pluginVersionId)}/clone`,
    method: 'POST' as const,
  }),
  promotePluginVersion: ({ pluginId, pluginVersionId }: { pluginId: string; pluginVersionId: string }) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version/${encodeURIComponent(pluginVersionId)}/promote`,
    method: 'PATCH' as const,
  }),
  /**
   * The template facts about a plugin: whether it is an instance, the template
   * version its version is pinned to, and the names of the events that pinned
   * template version binds.
   *
   * `pluginVersionId` is optional on the wire but we always send it. The
   * service defaults to the LATEST PUBLISHED version, and an instance is
   * created as a draft — so omitting it reports nothing for exactly the plugin
   * a vendor is most likely to be looking at.
   */
  /**
   * The variables a plugin version EFFECTIVELY declares. For a template
   * instance that is the pinned template version's variables merged with the
   * instance's own overrides — not just the ones it overrides, which for a
   * fresh instance is none.
   *
   * Names and flags only. The values are deliberately absent: a template's
   * variables were set by an admin, and plugin ownership does not entitle the
   * owner to read them (the sibling `.../variables` endpoint that does decrypt
   * them is closed to vendors for exactly this reason).
   */
  getPluginVariableNames: ({
    pluginId,
    pluginVersionId,
  }: {
    pluginId: string;
    pluginVersionId: string;
  }) => ({
    url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/version/${encodeURIComponent(pluginVersionId)}/variableNames`,
    method: 'GET' as const,
  }),
  getPluginMetadata: ({
    pluginId,
    pluginVersionId,
  }: {
    pluginId: string;
    pluginVersionId?: string;
  }) => {
    const qs = pluginVersionId
      ? `?pluginVersionId=${encodeURIComponent(pluginVersionId)}`
      : '';
    return {
      url: `${services.integration}/v1/plugin/${encodeURIComponent(pluginId)}/metadata${qs}`,
      method: 'GET' as const,
    };
  },
};

export const versionsApi = integrationApi.injectEndpoints({
  endpoints: (builder) => ({
    /** GET /integration/v1/event[?eventType=<type>] — event catalog. */
    getEventTypes: builder.query<EventTypes, { eventType?: string } | void>({
      query: (args) => versionRequestBuilders.getEventTypes(args),
      transformResponse: (response: EventTypes | { data?: EventTypes }): EventTypes => {
        const obj = response as { data?: EventTypes; eventsCatalog?: unknown };
        if (obj.eventsCatalog) return response as EventTypes;
        return obj.data ?? { eventsCatalog: [] };
      },
      providesTags: (_r, _e, args) => [
        { type: 'EventTypes', id: (args && 'eventType' in args && args.eventType) || 'ALL' },
      ],
    }),
    /**
     * GET /integration/v1/plugin/{id}/version/{vid} — full version detail
     * (variables + readOnlyVariableKeys + events + id/name/isDraft). Unwraps
     * the `pluginVersion` envelope and lifts integratorId/pluginName alongside.
     */
    getPluginVersionFull: builder.query<PluginVersionFull, { pluginId: string; pluginVersionId: string }>({
      query: (args) => versionRequestBuilders.getPluginVersionFull(args),
      transformResponse: (response: PluginVersionEnvelope | PluginVersionDetails): PluginVersionFull => {
        const env = response as PluginVersionEnvelope;
        if (env && env.pluginVersion) {
          return { ...env.pluginVersion, integratorId: env.integratorId, pluginName: env.pluginName };
        }
        return response as PluginVersionFull;
      },
      providesTags: (_r, _e, { pluginId, pluginVersionId }) => [
        { type: 'PluginVersions', id: `${pluginId}:${pluginVersionId}:full` },
        { type: 'PluginVersions', id: pluginId },
      ],
    }),
    /** POST /integration/v1/plugin/{id}/version — create the initial (draft) version. */
    createPluginVersion: builder.mutation<PluginVersionEnvelope, { pluginId: string; data: PluginVersionUpdate }>({
      query: (args) => versionRequestBuilders.createPluginVersion(args),
      invalidatesTags: (_r, _e, { pluginId }) => [{ type: 'PluginVersions', id: pluginId }],
    }),
    /** PATCH /integration/v1/plugin/{id}/version/{vid} — update an existing version. */
    updatePluginVersion: builder.mutation<
      PluginVersionEnvelope,
      { pluginId: string; pluginVersionId: string; data: PluginVersionUpdate }
    >({
      query: (args) => versionRequestBuilders.updatePluginVersion(args),
      invalidatesTags: (_r, _e, { pluginId }) => [{ type: 'PluginVersions', id: pluginId }],
    }),
    /** POST /integration/v1/plugin/{id}/version/{vid}/clone — clone a major version before editing. */
    clonePluginVersion: builder.mutation<PluginVersionEnvelope, { pluginId: string; pluginVersionId: string }>({
      query: (args) => versionRequestBuilders.clonePluginVersion(args),
      invalidatesTags: (_r, _e, { pluginId }) => [{ type: 'PluginVersions', id: pluginId }],
    }),
    /** PATCH /integration/v1/plugin/{id}/version/{vid}/promote — publish the version. */
    promotePluginVersion: builder.mutation<void, { pluginId: string; pluginVersionId: string }>({
      query: (args) => versionRequestBuilders.promotePluginVersion(args),
      invalidatesTags: (_r, _e, { pluginId }) => [{ type: 'PluginVersions', id: pluginId }],
    }),
    /**
     * GET /integration/v1/plugin/{id}/metadata — template facts about a plugin.
     * Answers with nulls for an ordinary plugin rather than erroring, so it is
     * safe to ask about anything.
     */
    /**
     * GET /integration/v1/plugin/{id}/version/{vid}/variableNames — the
     * effective variable set, names and flags only.
     */
    getPluginVariableNames: builder.query<
      PluginVariableNames,
      { pluginId: string; pluginVersionId: string }
    >({
      query: (args) => versionRequestBuilders.getPluginVariableNames(args),
      transformResponse: (
        response: PluginVariableNames | { data?: PluginVariableNames },
      ): PluginVariableNames => {
        const obj = response as { data?: PluginVariableNames };
        return obj?.data ?? (response as PluginVariableNames) ?? {};
      },
      providesTags: (_r, _e, { pluginId, pluginVersionId }) => [
        { type: 'PluginVersions', id: `${pluginId}:${pluginVersionId}:variableNames` },
        { type: 'PluginVersions', id: pluginId },
      ],
    }),
    getPluginMetadata: builder.query<PluginMetadata, { pluginId: string; pluginVersionId?: string }>({
      query: (args) => versionRequestBuilders.getPluginMetadata(args),
      transformResponse: (response: PluginMetadata | { data?: PluginMetadata }): PluginMetadata => {
        const obj = response as { data?: PluginMetadata };
        return obj?.data ?? (response as PluginMetadata) ?? {};
      },
      providesTags: (_r, _e, { pluginId, pluginVersionId }) => [
        { type: 'PluginVersions', id: `${pluginId}:${pluginVersionId ?? 'latest'}:metadata` },
      ],
    }),
  }),
});

export const {
  useGetEventTypesQuery,
  useLazyGetEventTypesQuery,
  useGetPluginVersionFullQuery,
  useLazyGetPluginVersionFullQuery,
  useCreatePluginVersionMutation,
  useUpdatePluginVersionMutation,
  useClonePluginVersionMutation,
  usePromotePluginVersionMutation,
  useGetPluginMetadataQuery,
  useGetPluginVariableNamesQuery,
} = versionsApi;
